import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { authApi } from '@/api/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { notifyError, notifySuccess } from '@/lib/feedback';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tokenError, setTokenError] = useState('');
  const [email, setEmail] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    let active = true;

    const validateToken = async () => {
      if (!token) {
        setTokenError('Token reset password tidak ditemukan.');
        setIsChecking(false);
        return;
      }

      try {
        const response = await authApi.validatePasswordResetToken(token);
        if (!active) {
          return;
        }

        setEmail(response.email ?? '');
      } catch (err) {
        if (!active) {
          return;
        }

        setTokenError('Token reset password tidak valid atau sudah kedaluwarsa.');
        notifyError('Token reset tidak valid', err, false);
      } finally {
        if (active) {
          setIsChecking(false);
        }
      }
    };

    void validateToken();

    return () => {
      active = false;
    };
  }, [token]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage('');

    if (password !== passwordConfirmation) {
      setTokenError('Konfirmasi password tidak sama.');
      return;
    }

    setTokenError('');
    setIsSubmitting(true);

    try {
      const response = await authApi.resetPassword({
        token,
        password,
        passwordConfirmation,
      });

      setSuccessMessage(response.message);
      notifySuccess('Password berhasil direset', response.message, false);
      window.setTimeout(() => navigate('/login', { replace: true }), 1200);
    } catch (err) {
      notifyError('Gagal reset password', err, false);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold">Reset password</CardTitle>
          <CardDescription>
            {email ? `Atur password baru untuk ${email}.` : 'Masukkan password baru untuk akun Anda.'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isChecking ? (
            <div className="text-sm text-muted-foreground">Memeriksa token reset password...</div>
          ) : tokenError ? (
            <div className="space-y-4">
              <Alert variant="destructive" className="items-center">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{tokenError}</AlertDescription>
              </Alert>
              <div className="text-center text-sm text-muted-foreground">
                <Link to="/forgot-password" className="underline underline-offset-4">
                  Minta link reset baru
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {successMessage && (
                <Alert className="items-center">
                  <AlertDescription>{successMessage}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="new-password">Password baru</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="Minimal 8 karakter"
                  disabled={isSubmitting}
                  required
                  minLength={8}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">Konfirmasi password baru</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={passwordConfirmation}
                  onChange={(event) => setPasswordConfirmation(event.target.value)}
                  placeholder="Ulangi password baru"
                  disabled={isSubmitting}
                  required
                  minLength={8}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isSubmitting}>
                {isSubmitting ? 'Menyimpan password...' : 'Simpan password baru'}
              </Button>
              <div className="text-center text-sm text-muted-foreground">
                <Link to="/login" className="underline underline-offset-4">
                  Kembali ke login
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
